/**
 * @created     2026-03-11
 * @updated     2026-10-24 - session id via X-Session-Id header, not in URL
 * @description Fetches knowledge base text and claim data from GET /api/knowledge.
 *              With X-Session-Id header returns DB-based akte data.
 *              Without session_id returns mock data (fallback).
 */
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ChatService } from './chat.service';

export function safePrivacyUrl(value: unknown): string | null {
  if (typeof value !== 'string' || !value || /[\s\u0000-\u001f\u007f\\]/.test(value)) return null;
  if (/^https?:\/\//i.test(value)) {
    try { return new URL(value).hostname ? value : null; } catch { return null; }
  }
  return !value.startsWith('//') && !value.includes(':') && !value.startsWith('#') ? value : null;
}

export interface AkteData {
  [key: string]: unknown;
  aktennummer: string;
  unterakte: string;
  pin?: string;
  standort_clz?: string;
  sd_name?: string;
  sd_strasse?: string;
  sd_plz?: string;
  sd_ort?: string;
  gl_name: string;
  gl_strasse: string;
  gl_plz: string;
  gl_ort: string;
  stadium: string;
  privatperson: boolean;
  gesamt_saldo: number;
  hauptforderung_saldo: number;
  zinsen_hf_saldo: number;
  zinsen_kosten_saldo: number;
  unverz_kosten_saldo: number;
  verz_kosten_saldo: number;
  rzv_erlaubt: boolean;
  std_erlaubt: boolean;
  vgl_erlaubt: boolean;
  rzv_sperre: boolean;
  std_sperre: boolean;
  vgl_sperre: boolean;
  hauptforderung_forderung: number;
  zinsen_hf_forderung?: number;
  zinsen_kosten_forderung?: number;
  unverz_kosten_forderung?: number;
  verz_kosten_forderung?: number;
  gesamt_forderung: number;
  hauptforderung_zahlung?: number;
  zinsen_hf_zahlung?: number;
  zinsen_kosten_zahlung?: number;
  unverz_kosten_zahlung?: number;
  verz_kosten_zahlung?: number;
  gesamt_zahlung?: number;
  taegl_zinsen_hf: number;
  taegl_zinsen_kosten: number;
  glsetting_rzv_erlaubt_g?: boolean | number | null;
  glsetting_rzv_erlaubt_v?: boolean | number | null;
  glsetting_rzv_erlaubt_ue?: boolean | number | null;
  glsetting_std_erlaubt_g?: boolean | number | null;
  glsetting_std_erlaubt_v?: boolean | number | null;
  glsetting_std_erlaubt_ue?: boolean | number | null;
  glsetting_vgl_erlaubt_g?: boolean | number | null;
  glsetting_vgl_erlaubt_v?: boolean | number | null;
  glsetting_vgl_erlaubt_ue?: boolean | number | null;
}

export interface StandortData {
  [key: string]: unknown;
  clz: string;
  name: string;
  strasse: string;
  plz: string;
  ort: string;
  telefon: string;
  email: string;
  webseite: string;
  bank_name: string;
  bank_iban: string;
  bank_bic: string;
  oeffnungszeiten: Record<string, unknown>;
}

export interface RechnungData {
  [key: string]: unknown;
  rechnungsbetrag: number;
  rechnungsdatum: string;
  rechnungsnummer: string;
  forderungsgrund: string;
}

export interface ZahlungData {
  [key: string]: unknown;
  zahlungsbetrag: number;
  zahlungsdatum: string;
  event_code: string;
}

export interface RateData {
  [key: string]: unknown;
  anzahl_raten: number;
  zahlungsart: string;
  aktuelle_rate_datum: string;
  aktuelle_rate_hoehe: number;
  naechste_rate_datum: string;
  naechste_rate_hoehe: number;
  letzte_rate_datum: string;
  letzte_rate_hoehe: number;
}

export interface TitelData {
  [key: string]: unknown;
  titelbetrag?: number;
  titel_verzinsliche_kosten?: number;
  titel_unverzinsliche_kosten?: number;
  titel_zinsen?: number;
  titel_hauptforderung?: number;
  titeldatum?: string;
  titelnummer?: string;
  titelbezeichnung?: string;
  titelgericht?: string;
  titel_art?: string;
  titel_datum?: string;
  gericht?: string;
  court?: string;
  aktenzeichen_gericht?: string;
}

export interface DetailedClaimStatementData {
  [key: string]: unknown;
  raw?: Record<string, unknown>;
  description?: string;
  amount?: number | string | null;
  amount_text?: string;
  date?: string | null;
  main_claim_amount?: number | string | null;
  main_claim_amount_text?: string;
  interest_amount?: number | string | null;
  interest_amount_text?: string;
  non_interest_costs_amount?: number | string | null;
  non_interest_costs_amount_text?: string;
  interest_costs_amount?: number | string | null;
  interest_costs_amount_text?: string;
  claim_amount?: number | string | null;
  claim_amount_text?: string;
  payment_amount?: number | string | null;
  payment_amount_text?: string;
  bezeichnung?: string;
  betrag?: string;
  datum?: string;
}

export interface ClaimDetailsData {
  [key: string]: unknown;
  breakdown?: {
    [key: string]: unknown;
    detailed?: DetailedClaimStatementData[];
  };
}

export interface PermissionsData {
  [key: string]: unknown;
  rzv_erlaubt: boolean;
  std_erlaubt: boolean;
  vgl_erlaubt: boolean;
}

export interface KnowledgeData {
  [key: string]: unknown;
  source: 'db' | 'mock';
  // DB-basiert
  akte?: AkteData;
  raw_case_json?: Record<string, unknown> | null;
  standort?: StandortData;
  rechnungen?: RechnungData[];
  zahlungen?: ZahlungData[];
  titel?: TitelData[];
  raten?: RateData[];
  claim_details?: ClaimDetailsData;
  permissions?: PermissionsData;
  // Mock-Fallback
  knowledge?: string;
  mockData?: Record<string, unknown>;
}

interface ApiResponse {
  success: boolean;
  data: KnowledgeData;
}

@Injectable({ providedIn: 'root' })
export class KnowledgeService {
  private readonly http = inject(HttpClient);
  private readonly chatService = inject(ChatService);
  private readonly apiUrl = '/api/knowledge';

  getPrivacyUrl(standortClz = ''): Observable<string | null> {
    const url = standortClz ? `/api/privacy?standortClz=${encodeURIComponent(standortClz)}` : '/api/privacy';
    return this.http.get<{ data: { privacyDocumentUrl: unknown } }>(url).pipe(
      map(res => safePrivacyUrl(res.data.privacyDocumentUrl))
    );
  }

  /** Always recheck the current server policy before returning case data. */
  getKnowledge(): Observable<KnowledgeData> {
    const sessionId = this.chatService.getSessionId();
    const headers = sessionId ? { 'X-Session-Id': sessionId } : undefined;
    return this.http.get<ApiResponse>(this.apiUrl, { headers }).pipe(map(res => res.data));
  }
}
